import { useEffect } from "react";

const ROOT_SELECTOR = "#root";

/** Skip our hardening for specific subtrees (e.g. a future login helper). */
const OPT_OUT_SELECTOR = "[data-allow-browser-autocomplete='true']";

const READONLY_GUARD_SKIP_TYPES = new Set([
  "hidden",
  "submit",
  "button",
  "checkbox",
  "radio",
  "file",
  "image",
  "reset",
  "range",
  "color",
  "date",
  "datetime-local",
  "month",
  "week",
  "time",
  "search",
]);

const readonlyGuarded = new WeakSet();

function isOptOut(el) {
  return el.closest(OPT_OUT_SELECTOR);
}

function shouldUseReadonlyGuard(el) {
  if (el.tagName !== "INPUT" && el.tagName !== "TEXTAREA") return false;
  if (el.tagName === "INPUT") {
    const type = (el.getAttribute("type") || "text").toLowerCase();
    if (READONLY_GUARD_SKIP_TYPES.has(type)) return false;
  }
  return true;
}

function attachReadonlyUntilFocus(el) {
  if (readonlyGuarded.has(el)) return;
  readonlyGuarded.add(el);
  el.setAttribute("readonly", "readonly");
  const onFocus = () => {
    el.removeAttribute("readonly");
    el.removeEventListener("focus", onFocus);
  };
  el.addEventListener("focus", onFocus);
}

function applyAutocompleteOff(root) {
  if (!root) return;

  root.querySelectorAll("form").forEach((el) => {
    if (isOptOut(el)) return;
    if (el.getAttribute("autocomplete") !== "off") {
      el.setAttribute("autocomplete", "off");
    }
  });

  root.querySelectorAll("input, textarea, select").forEach((el) => {
    if (isOptOut(el)) return;

    const type = (el.getAttribute("type") || "").toLowerCase();
    if (el.tagName === "INPUT" && type === "hidden") return;

    if (el.getAttribute("autocomplete") !== "off") {
      el.setAttribute("autocomplete", "off");
    }

    if (shouldUseReadonlyGuard(el)) {
      attachReadonlyUntilFocus(el);
    }
  });
}

/**
 * Aggressively discourages browser autofill (Chrome often ignores autocomplete="off" alone).
 * Uses autocomplete="off", form-level off, and readonly-until-focus on text-like fields.
 * Opt out a subtree with data-allow-browser-autocomplete="true".
 */
export function useDisableBrowserAutocomplete() {
  useEffect(() => {
    const root = document.querySelector(ROOT_SELECTOR);
    if (!root) return;

    let enabled = false;
    let raf = 0;
    const schedule = () => {
      if (!enabled) return;
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        applyAutocompleteOff(root);
      });
    };

    // Root layout hydrates first and must not mutate streamed page HTML
    // before those client components finish hydrating.
    const enableTimer = window.setTimeout(() => {
      enabled = true;
      applyAutocompleteOff(root);
    }, 300);

    const observer = new MutationObserver(schedule);
    observer.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["autocomplete"],
    });

    return () => {
      enabled = false;
      window.clearTimeout(enableTimer);
      if (raf) cancelAnimationFrame(raf);
      observer.disconnect();
    };
  }, []);
}
